import { Avatar, Button, Grid, Stack, Typography } from '@mui/material';
import { ButtonLinkOrDownload } from 'Components';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { NewIcon, NewLabelIcon, UploadPrescription } from 'Images';
import { MedicalReferralListMobile } from './MedicalReferralListMobile';
import {
  IUserUpdates,
  NotifSubjectsType,
  setNotifReaded,
} from 'API/Home/userUpdates';
import { shortDateFormat } from 'Config';
import { format } from 'date-fns';
import { useLoader } from 'Hooks/useLoader';
import { getFile } from 'API';
import { downloadFile } from 'Utils';
import { useEffect, useState } from 'react';
import { requestFile } from 'API/medicalReferrals';
import { useNavigate } from 'react-router-dom';
import { sendEvent } from 'Utils/sendEvent';
import {
  getUpdateGeneralDataByTypeId,
  shouldHaveTwoLinks,
  shouldPopup,
} from 'Utils/updates';
import { OneOrTwoButtons } from 'Components/OneOrTwoButtons';

interface IRowProps {
  data: IUserUpdates;
  setShowDialog?: () => void;
}

export const Row = ({ data, setShowDialog }: IRowProps) => {
  const { isLoading, loader, setIsLoading } = useLoader('scale(0.5)');
  const [isRead, setIsRead] = useState<Boolean>(data.readedDate ? true : false);

  const dictionary = useDictionary(Dictionary, DICTIONARY.Home).userUpdates;
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();

  const download = async (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    setIsLoading(true);
    sendEvent('Home page', 'UpdatesCta', data.title, 'Download');
    setIsRead(true);
    await setNotifReaded(data.id);
    e.stopPropagation();
    let file;
    if (data.notifEntityId === NotifSubjectsType.MedicalReferrals) {
      file = await requestFile(jsonData.Items[0].Id);
    } else if (data.notifEntityId === NotifSubjectsType.Documents) {
      file = await getFile(jsonData.Items[0].Id);
    }
    setIsLoading(false);
    if (file && file.fileContentBase64) {
      downloadFile(
        file.fileContentBase64,
        'pdf',
        file.documentName || 'New Document'
      );
    }
  };

  const handleNavigate = async () => {
    sendEvent(
      'Home page',
      'UpdatesCta',
      data.title,
      `Navigate to ${secondLinkUrl || url}`
    );
    navigate(secondLinkUrl || url);
    setIsRead(true);
    await setNotifReaded(data.id);
  };

  useEffect(() => {
    setIsLoading(false);
  }, [setIsLoading]);

  const jsonData = data.entityJSON && JSON.parse(data.entityJSON);
  const applyNumber = jsonData?.Items[0].ApplyNumber;
  const prescCode = jsonData?.Items[0].PrescCode;
  const requestNumber = jsonData?.Items[0].RequestNumber;

  const { label, url, secondLinkLabel, secondLinkUrl } =
    getUpdateGeneralDataByTypeId(
      data.notifEntityId,
      applyNumber,
      data.notifSubjectId,
      prescCode,
      requestNumber
    );
  const isTwoLinks = shouldHaveTwoLinks(data.notifSubjectId);
  const shouldShowPopup = shouldPopup(jsonData, data.notifEntityId);

  const isDownload =
    jsonData?.Title === null &&
    jsonData?.Items[0].Id &&
    (data.notifEntityId === NotifSubjectsType.MedicalReferrals ||
      data.notifEntityId === NotifSubjectsType.Documents);

  return (
    <Grid
      container
      sx={{
        borderBottom: '1px solid',
        borderColor: 'primary.shade_100',
        p: isDesktop ? '24px 8px' : '24px 12px',
      }}
    >
      <Grid item xs={12} md={5}>
        <Stack flexDirection={'row'}>
          <Typography variant='body1'>{data.title}</Typography>
          {!isRead && (
            <Avatar
              src={isDesktop ? NewIcon : NewLabelIcon}
              alt=''
              sx={
                isDesktop
                  ? { width: '44px', height: '20px', m: '0 8px' }
                  : {
                      width: '42px',
                      height: '26px',
                      position: 'absolute',
                      right: '-8px',
                    }
              }
            />
          )}
        </Stack>
        <Stack width='fit-content'>
          <Typography
            variant='body6'
            dangerouslySetInnerHTML={{ __html: data.description }}
          />
          {isDesktop && shouldShowPopup && (
            <Button
              sx={{
                width: 'fit-content',
                fontSize: '16px',
                color: 'primary.main',
                fontWeight: 600,
                p: 0,
              }}
              onClick={() => setShowDialog && setShowDialog()}
            >
              {dictionary.toDownloadMedicalReferrals}
            </Button>
          )}
        </Stack>
      </Grid>
      <Grid item xs={12} md={2}>
        <Stack
          sx={
            isDesktop
              ? { m: '0 32px' }
              : { flexDirection: 'row', m: '12px 0', alignItems: 'center' }
          }
        >
          <Typography
            variant='body3'
            sx={{ mr: isDesktop ? undefined : '6px' }}
          >
            {Dictionary[DICTIONARY.Home].userUpdates.date}
          </Typography>
          <Typography variant='body5'>
            {data.createDate &&
              format(new Date(data.createDate), shortDateFormat)}
          </Typography>
        </Stack>
        {shouldShowPopup && (
          <MedicalReferralListMobile items={jsonData?.Items} />
        )}
      </Grid>
      <Grid item xs={12} md={5}>
        {isLoading ? (
          <Stack height={'20px'} justifyContent={'center'}>
            {loader}
          </Stack>
        ) : isTwoLinks ? (
          <OneOrTwoButtons
            width={isTwoLinks ? '100%' : 'fit-content'}
            leftButton={{
              label: label,
              onClick: () => handleNavigate,
            }}
            rightButton={
              secondLinkLabel
                ? {
                    label: secondLinkLabel,
                    onClick: () => handleNavigate,
                    icon: UploadPrescription,
                  }
                : undefined
            }
          />
        ) : (
          <ButtonLinkOrDownload
            label={label}
            url={url}
            downloadFile={isDownload && download}
            isDownload={isDownload}
            isCallbackNavigation
            callbackNavigation={handleNavigate}
            isWidthBtn
          />
        )}
      </Grid>
    </Grid>
  );
};
